<script>
  import {onDestroy, onMount, setContext} from 'svelte'
  import {media} from 'svelte-match-media'
  import gsap from 'gsap'
  import ScrollTrigger from 'gsap/ScrollTrigger'

  import AnimatedTitle from '../components/AnimatedTitle.svelte'
  import FramedImage from '../components/FramedImage.svelte'
  import WifiFan from '../svgs/WifiFan.svelte'

  const framedImageController = {
    timeline: true
  }
  const frameTitles = [
    'Get a unified view of delivery across all networks',
    'Access more real-time information',
    'Make smarter adjustments to your upfront and scatter buys throughout the year',
    'Easily view full-funnel impact',
    'Better understand the overlap between buys, enabling media cost savings'
  ]
  setContext('framedImageController', framedImageController)

  let section, tl, st, animatedOnce = false

  function useCompactLayout() {
    return document.documentElement.clientWidth >= 768 && document.documentElement.clientHeight < 960
  }

  onMount(() => {
    createTimeline()

    st = ScrollTrigger.create({
      trigger: $media.mobile ? section.querySelector('.tv-and-remote') : section.querySelector('.tv-outer'),
      scrub: 0.5,
      pin: true,
      anticipatePin: true,
      start: 'top top+=120',
      end: '+=2500',
      animation: tl,
      pinSpacing: false,
      onEnter: () => {
        animatedOnce = true
      },
      onRefresh: () => {
        if(animatedOnce) {
          createTimeline()
        }
      }
    })
  })

  function createTimeline() {
    if(tl == null) tl = gsap.timeline()
    else tl.totalTime(0).clear()
    const from1 = {
      opacity: 0,
      ease: 'power1.out',
      duration: 0.7
    }
    const to1 = {
      opacity: 1
    }
    if(useCompactLayout()) {
      from1.x = -500
      from1.y = 120
      to1.x = 0
      to1.y = 0
    } else {
      from1.x = 0
      from1.y = 0
    }
    tl.fromTo(section.querySelector('.remote'), from1, to1, 0)
    const from2 = {
      rotate: 70,
    }
    if(!useCompactLayout()) {
      from2.y = -320
    } else {
      from2.y = 0
    }

    tl.fromTo(section.querySelector('.remote'), from2, {
      rotate: 0,
      y: 0,
      ease: 'power2.inOut',
      duration: 0.9
    }, 1)
    tl.fromTo(section.querySelector('.btn-before'), {opacity: 1}, {opacity: 0, duration: 0.2}, 1.75)
    tl.fromTo(section.querySelector('.btn-after'), {opacity: 0}, {opacity: 1, duration: 0.2}, 1.85)
    tl.fromTo(section.querySelector('.wifi-fan'), {
      opacity: 0,
      y: 30,
      scale: 0.7,
      transformOrigin: '50% 100%',
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      ease: 'power2.out',
      duration: 0.5
    }, 1.8)
    if(animatedOnce) framedImageController.rebuild()
    framedImageController.timeline.duration(1)
    tl.add(framedImageController.timeline, 1.5)
    let prevItem, delay = tl.duration() - 0.75
    section.querySelectorAll('.tv .titles-wrap .item').forEach((item, index) => {
      if(prevItem) {
        tl.to(prevItem, {opacity: 0, y: $media.mobile ? -20 : -40, ease: 'power2.inOut', duration: 0.5}, delay + index * .5)
        tl.to(section.querySelector('.btn-before'), {opacity: 1, duration: 0.1}, delay + index * .5)
        tl.to(section.querySelector('.btn-before'), {opacity: 1, duration: 0.1}, delay + index * .5 + .2)
        tl.to(section.querySelector('.btn-after'), {opacity: 0, duration: 0.1}, delay + index * .5)
        tl.to(section.querySelector('.btn-after'), {opacity: 1, duration: 0.1}, delay + index * .5 + .2)
        tl.to(section.querySelectorAll('.wifi-fan .arc'), {opacity: 0, duration: 0.3, stagger: .05}, delay + index * .5 - .2)
        tl.to(section.querySelectorAll('.wifi-fan .arc'), {opacity: 1, duration: 0.3, stagger: .05}, delay + index * .5 + .1)
      }
      tl.fromTo(item, {opacity: 0, y: $media.mobile ? 30 : 80}, {opacity: 1, y: 0, ease: 'power2.inOut', duration: 0.5}, delay + index * .5)
      prevItem = item
      delay += 1
    })
    tl.add(() => {}, '+=1')
  }

  onDestroy(() => {
    tl?.pause().kill()
    st?.kill()
  })

</script>
<section class="part2" bind:this={section}>
  <div class="tv-and-remote">
    <div class="rgrid-md-3-4">
      <div class="col title-col">
        <AnimatedTitle class="large title1 mobile-h1-medium" style="maskup">Using a DSP unlocks a suite of benefits.</AnimatedTitle>
      </div>
    </div>
    <div class="rgrid-md-12-col tv-outer">
      <div class="tv-col">
        <FramedImage
          titles={frameTitles}
          mobile="https://res.cloudinary.com/tanka-design/image/upload/c_scale,h_370,q_auto/v1616163348/VerizonMediaATV/tv-image1-desktop_csjn6l.jpg"
          desktop="https://res.cloudinary.com/tanka-design/image/upload/q_auto/v1616163348/VerizonMediaATV/tv-image1-desktop_csjn6l.jpg"
          alt="Couple on couch pointing remote"
          />
      
        <div class="remote-control">
          <div class="rc-col1">
            <div class="wifi-fan-wrap">
              <WifiFan />
            </div>
          </div>
          <div class="rc-col2">
            <svg class="remote" width="37" height="120" viewBox="0 0 37 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="26.5383" cy="42.6921" r="5.26923" class="outlines btn-before" />
              <circle cx="26.5383" cy="42.6921" r="5.26923" class="button btn-after" />
              <g class="outlines">
                <circle cx="10.3845" cy="42.6921" r="5.26923"/>
                <circle cx="10.3845" cy="60.0002" r="5.26923"/>
                <circle cx="10.3845" cy="73.8464" r="5.26923"/>
                <rect x="1" y="1" width="34.9231" height="118" rx="4" stroke-width="2"/>
                <rect x="20.1152" y="54.731" width="10.5385" height="24.3846" rx="5.26923"/>
              </g>
              <g class="fills">
                <rect x="1.15381" y="50.769" width="34.6154" height="1.15385" fill="white"/>
                <rect x="13.8462" y="4.61523" width="9.23077" height="1.15385" rx="0.576923" fill="white"/>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
      
</section>

<style>
  .remote-control {
    margin: 40px 0;
  }
  .remote-control :global(.wifi-fan) {
    width: 60px;
    height: 60px;
    margin: auto;
  }
  .remote {
    margin: 0 auto;
  }
  .remote .outlines {
    stroke: white
  }
  .remote .fills {
    fill: white
  }
  .remote .button {
    fill: var(--verizon-red);
    stroke: var(--verizon-red);
  }
  .tv-outer {
    padding-bottom: 2500px;
  }
  @media(min-width: 768px) {
    .tv-col {
      grid-column: 3 / span 8
    }
    .remote {
      width: 62px;
      height: auto;
    }
    .remote-control :global(.wifi-fan) {
      width: 98px;
      height: auto;
    }
  }
  @media(min-width: 768px) and (max-height: 960px) {
    .tv-col {
      display: grid;
      grid-column: span 12;
      grid-template-columns: auto 160px;
      grid-gap: var(--gutter-lg);
      max-width: 900px;
      width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
    .remote-control {
      display: flex;
      align-items: center;
      margin: 0;
    }
    .remote-control :global(.wifi-fan) {
      width: 100%;
      max-width: 90px;
      margin: 0 0 0 auto;
      height: auto;
    }
    .remote-control .wifi-fan-wrap {
      transform: rotate(-90deg);
    }
    .remote {
      margin: 0 0 0 10px;
    }
    .tv-outer {
      margin-top: -300px;
    }
    .tv-and-remote .title-col {
      z-index: 2;
      position: relative;
    }
  }
</style>